import type { Sheet, SheetRow } from 'chery-contracts';
import { FunnelBars } from '../../components/ui/FunnelBars';
import { StackedColumns } from '../../components/ui/StackedColumns';
import { StatTile } from '../../components/ui/StatTile';
import { cn } from '../../lib/cn';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { formatDayMonth } from '../system/system-display';
import { fillPercent, funnelWidths, keyList, metricText, percentText, stackColumns, TARGET_MARKER_AT, TARGET_TRACK_PCT } from './board-display';

export interface SheetCardProps {
  sheet: Sheet;
}

const valueOf = (row: SheetRow): string => metricText(row.value, row.unit, row.currency);

const targetOf = (row: SheetRow): string | null => (row.target ? metricText(row.target.value, row.unit, row.currency) : null);

/**
 * One report sheet of plan §8.3, whatever its kind — funnel, tiles, bars-vs-target, stacked weekly columns or a
 * table — over one payload shape. Every figure is the server's; an unbound slot reads `—` with a hint, never 0.
 */
export const SheetCard = ({ sheet }: SheetCardProps) => {
  const { t } = useI18nTranslation();
  const unboundHint = t('board.unboundShort');

  const targetLine = (row: SheetRow): string => {
    if (!row.kpi) {
      return unboundHint;
    }

    if (!row.target) {
      return `${t(`board.sheets.aggregation.${row.aggregation}`)} · ${t('board.sheets.noTarget')}`;
    }

    return `${t('board.sheets.target', { value: targetOf(row) })} · ${t('board.sheets.completion', { pct: percentText(row.completionPct) })}`;
  };

  const body = (() => {
    switch (sheet.kind) {
      case 'FUNNEL': {
        const widths = funnelWidths(sheet.rows);

        return (
          <FunnelBars
            aria-label={sheet.title}
            stages={sheet.rows.map((row, index) => ({
              key: row.key,
              label: row.label,
              valueText: valueOf(row),
              widthPct: widths[index] ?? 0,
              conversionText: row.previousStagePct === null ? null : t('board.sheets.conversion', { pct: percentText(row.previousStagePct) }),
            }))}
          />
        );
      }
      case 'TILES':
        return (
          <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
            {sheet.rows.map((row) => (
              <StatTile
                key={row.key}
                label={row.label}
                value={valueOf(row)}
                sub={
                  row.target && row.completionBasis === 'TOTAL' && sheet.key === 'DELIVERIES_VS_GATE'
                    ? `${t('board.sheets.gateTarget', { name: row.target.name, value: targetOf(row) })} · ${t('board.sheets.completion', { pct: percentText(row.completionPct) })}`
                    : targetLine(row)
                }
                hint={row.kpi ? undefined : unboundHint}
              />
            ))}
          </div>
        );
      case 'BARS_VS_TARGET':
        return (
          <ul className="space-y-2">
            {sheet.rows.map((row) => (
              <li
                key={row.key}
                className="grid grid-cols-[9rem_minmax(0,1fr)_8rem] items-center gap-2 text-xs"
                data-bar-row={row.key}
                title={`${row.label}: ${valueOf(row)} · ${targetLine(row)}`}
              >
                <span className="truncate text-muted-foreground">{row.label}</span>
                <span className="relative h-4 rounded-sm bg-muted">
                  {row.completionPct !== null ? (
                    <span
                      className="absolute inset-y-0 start-0 rounded-e-[4px] rounded-s-sm"
                      style={{ width: `${fillPercent(row.completionPct, TARGET_TRACK_PCT)}%`, background: 'var(--series-1)' }}
                    />
                  ) : null}
                  {row.target ? (
                    <span
                      aria-hidden
                      className="absolute -inset-y-0.5 w-0.5 bg-foreground"
                      style={{ insetInlineStart: `${TARGET_MARKER_AT}%` }}
                    />
                  ) : null}
                </span>
                <span className="text-end">
                  <span className="block font-mono font-semibold tabular-nums text-foreground">{valueOf(row)}</span>
                  <span className={cn('block text-[11px] text-muted-foreground', !row.kpi && 'italic')}>
                    {!row.kpi ? t('board.bindings.unbound') : row.target ? percentText(row.completionPct) : t('board.sheets.noTarget')}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        );
      case 'STACKED_COLUMNS': {
        const { columns, max } = stackColumns(sheet.rows);
        const unit = sheet.rows[0];

        return (
          <StackedColumns
            aria-label={sheet.title}
            series={sheet.rows.map((row) => ({ key: row.key, label: row.kpi ? row.label : `${row.label} (${t('board.bindings.unbound')})` }))}
            columns={columns.map((column) => ({ ...column, label: formatDayMonth(column.weekDate) }))}
            max={max}
            formatValue={(value) => metricText(value, unit?.unit ?? 'NUMBER', unit?.currency ?? null)}
          />
        );
      }
      case 'TABLE':
        return (
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr className="border-b border-border text-muted-foreground">
                <th className="py-1.5 pe-2 text-start font-medium">{t('board.sheets.columns.metric')}</th>
                <th className="px-2 py-1.5 text-end font-medium">{t('board.sheets.columns.value')}</th>
                <th className="px-2 py-1.5 text-end font-medium">{t('board.sheets.columns.target')}</th>
                <th className="ps-2 py-1.5 text-end font-medium">{t('board.sheets.columns.completion')}</th>
              </tr>
            </thead>
            <tbody>
              {sheet.rows.map((row) => (
                <tr
                  key={row.key}
                  className="border-b border-border last:border-b-0"
                  data-table-row={row.key}
                >
                  <td className="py-1.5 pe-2">
                    <span className="text-foreground">{row.label}</span>
                    <span className="ms-1 text-[11px] text-muted-foreground">
                      · {row.kpi ? t(`board.sheets.aggregation.${row.aggregation}`) : <span className="italic">{unboundHint}</span>}
                    </span>
                  </td>
                  <td
                    className="px-2 py-1.5 text-end font-mono tabular-nums"
                    data-cell="value"
                  >
                    {valueOf(row)}
                  </td>
                  <td
                    className="px-2 py-1.5 text-end font-mono tabular-nums"
                    data-cell="target"
                  >
                    {targetOf(row) ?? '—'}
                  </td>
                  <td
                    className="ps-2 py-1.5 text-end font-mono tabular-nums"
                    data-cell="completion"
                  >
                    {percentText(row.completionPct)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        );
    }
  })();

  return (
    <section
      className="print-avoid-break flex flex-col gap-3 rounded-lg border border-border bg-card p-4"
      data-sheet={sheet.key}
      data-sheet-kind={sheet.kind}
    >
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-foreground">{sheet.title}</h3>
        <span className="text-[11px] text-muted-foreground">{sheet.period.label}</span>
      </header>
      {body}
      {sheet.unbound.length > 0 ? (
        <p
          className="text-[11px] italic text-muted-foreground"
          data-sheet-unbound
        >
          {t('board.sheets.unboundCount', { count: sheet.unbound.length, keys: keyList(sheet.unbound) })}
        </p>
      ) : null}
    </section>
  );
};
